import { api } from './apiClient';

// List quiz questions
export const fetchQuizList = async (
  page = 1,
  limit = 10,
  searchtext = '',
  filters = {}
) => {
  try {
    const body = {
      page,
      limit,
      searchtext: searchtext || '',
      filters,
    };

    const response = await api.post('/api/v1/admin/quiz-questions/list', body);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.message ||
        `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Create quiz question
export const createQuizQuestion = async (quizData) => {
  try {
    const payload = {
      content_id: quizData.content_id || quizData.contentId || '',
      question_type: quizData.question_type || 'text_text',
      question_text: quizData.question_text || quizData.question || '',
      difficulty_level: ['easy', 'medium', 'hard'].includes(quizData.difficulty_level)
        ? quizData.difficulty_level
        : 'medium',
      points: Number(quizData.points ?? quizData.points ?? 10),
      order: Number(quizData.order ?? 1),
      explanation: quizData.explanation || '',
    };

    // Include question image URL when provided (for image_text type or others)
    if (quizData.question_image_url) {
      payload.question_image_url = quizData.question_image_url;
    }

    // Handle reorder_text type
    if (quizData.question_type === 'reorder_text') {
      payload.reorder_correct_order = (quizData.reorder_correct_order || [])
        .map(item => typeof item === 'string' ? item : (item.text || ''))
        .filter(text => text.trim());
    }

    // Handle fill_blank_text type
    if (quizData.question_type === 'fill_blank_text') {
      payload.fill_blank_correct_order = (quizData.fill_blank_correct_order || []).map(
        (seg) => ({ title: seg.title ?? '', isBlank: Boolean(seg.isBlank) })
      );
    }

    // Handle text_text, audio_text, audio_audio and image_text type with options
    if (
      quizData.question_type === 'text_text' ||
      quizData.question_type === 'audio_text' ||
      quizData.question_type === 'audio_audio' ||
      quizData.question_type === 'image_text' ||
      !quizData.question_type
    ) {
      let options = [];

      if (Array.isArray(quizData.options) && quizData.options.length > 0) {
        // New format: options array with text and is_correct
        let order = 1;
        const validOptions = quizData.options.filter(opt => opt.text && opt.text.trim());
        const correctIndex = quizData.correct_answer_index !== undefined ? quizData.correct_answer_index : 0;

        validOptions.forEach((option, index) => {
          options.push({
            option_text: option.text.trim(),
            is_correct: index === correctIndex,
            order: order++,
          });
        });
      } else {
        // Legacy format: individual option fields
        const optionMap = {
          A: quizData.option_a_text || quizData.optionA || '',
          B: quizData.option_b_text || quizData.optionB || '',
          C: quizData.option_c_text || quizData.optionC || '',
          D: quizData.option_d_text || quizData.optionD || '',
        };

        const correctAnswer = quizData.correct_answer || 'A';
        let order = 1;

        ['A', 'B', 'C', 'D'].forEach((key) => {
          const optionText = optionMap[key];
          if (optionText && optionText.trim()) {
            options.push({
              option_text: optionText.trim(),
              is_correct: key === correctAnswer,
              order: order++,
            });
          }
        });
      }
      payload.options = options;
    }

    // Handle match_pairs type
    if (quizData.question_type === 'match_pairs') {
      if (Array.isArray(quizData.match_pairs) && quizData.match_pairs.length > 0) {
        payload.match_pairs = quizData.match_pairs
          .filter(pair => pair.left_item?.trim() && pair.right_item?.trim())
          .map((pair, index) => ({
            left_item: pair.left_item.trim(),
            right_item: pair.right_item.trim(),
            order: pair.order !== undefined ? pair.order : index,
          }));
      }
    }

    const response = await api.post('/api/v1/admin/quiz-questions', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.message ||
        `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Update quiz question (can be used for full edit or simple status toggle)
export const updateQuizQuestion = async (quizData) => {
  if (!quizData?.question_id && !quizData?.id) {
    throw new Error('Question ID is required to update a quiz');
  }

  try {
    const payload = {
      question_id: quizData.question_id || quizData.id,
    };

    // Only include fields that are explicitly provided
    if (quizData.content_id !== undefined) payload.content_id = quizData.content_id;
    if (quizData.question_type !== undefined)
      payload.question_type = quizData.question_type;
    if (quizData.question_text !== undefined)
      payload.question_text = quizData.question_text;
    if (quizData.explanation !== undefined)
      payload.explanation = quizData.explanation;
    if (quizData.points !== undefined)
      payload.points = Number(quizData.points);
    else if (quizData.points !== undefined)
      payload.points = Number(quizData.points); // Fallback for backward compatibility
    if (quizData.order !== undefined)
      payload.order = Number(quizData.order);
    if (typeof quizData.is_active === 'boolean')
      payload.is_active = quizData.is_active;
    if (['easy', 'medium', 'hard'].includes(quizData.difficulty_level))
      payload.difficulty_level = quizData.difficulty_level;

    // Include question image URL when explicitly provided
    if (quizData.question_image_url !== undefined) {
      payload.question_image_url = quizData.question_image_url;
    }

    // Handle reorder_text type
    if (quizData.question_type === 'reorder_text' && quizData.reorder_correct_order !== undefined) {
      payload.reorder_correct_order = (quizData.reorder_correct_order || [])
        .map(item => typeof item === 'string' ? item : (item.text || ''))
        .filter(text => text.trim());
    }

    // Handle fill_blank_text type
    if (quizData.question_type === 'fill_blank_text' && quizData.fill_blank_correct_order !== undefined) {
      payload.fill_blank_correct_order = (quizData.fill_blank_correct_order || []).map(
        (seg) => ({ title: seg.title ?? '', isBlank: Boolean(seg.isBlank) })
      );
    }

    // Handle text_text, audio_text, audio_audio and image_text type with options
    if (
      quizData.question_type === 'text_text' ||
      quizData.question_type === 'audio_text' ||
      quizData.question_type === 'audio_audio' ||
      quizData.question_type === 'image_text' ||
      (!quizData.question_type && Array.isArray(quizData.options))
    ) {
      if (Array.isArray(quizData.options) && quizData.options.length > 0) {
        // New format: options array with text
        let order = 1;
        const validOptions = quizData.options.filter(opt => opt.text && opt.text.trim());
        const correctIndex = quizData.correct_answer_index !== undefined ? quizData.correct_answer_index : 0;

        const options = validOptions.map((option, index) => ({
          option_text: option.text.trim(),
          is_correct: index === correctIndex,
          order: order++,
        }));

        if (options.length > 0) {
          payload.options = options;
        }
      } else {
        // Legacy format: individual option fields
        const hasOptionFields =
          quizData.option_a_text !== undefined ||
          quizData.option_b_text !== undefined ||
          quizData.option_c_text !== undefined ||
          quizData.option_d_text !== undefined ||
          quizData.correct_answer !== undefined;

        if (hasOptionFields) {
          const optionMap = {
            A: quizData.option_a_text || '',
            B: quizData.option_b_text || '',
            C: quizData.option_c_text || '',
            D: quizData.option_d_text || '',
          };

          const correctAnswer = quizData.correct_answer || 'A';
          const options = [];
          let order = 1;

          ['A', 'B', 'C', 'D'].forEach((key) => {
            const optionText = optionMap[key];
            if (optionText && optionText.trim()) {
              options.push({
                option_text: optionText.trim(),
                is_correct: key === correctAnswer,
                order: order++,
              });
            }
          });

          if (options.length > 0) {
            payload.options = options;
          }
        }
      }
    }

    // Handle match_pairs type
    if (quizData.question_type === 'match_pairs') {
      if (Array.isArray(quizData.match_pairs) && quizData.match_pairs.length > 0) {
        payload.match_pairs = quizData.match_pairs
          .filter(pair => pair.left_item?.trim() && pair.right_item?.trim())
          .map((pair, index) => ({
            left_item: pair.left_item.trim(),
            right_item: pair.right_item.trim(),
            order: pair.order !== undefined ? pair.order : index,
          }));
      }
    }

    const response = await api.put('/api/v1/admin/quiz-questions', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.message ||
        `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// Delete quiz question
export const deleteQuizQuestion = async (questionId) => {
  if (!questionId) {
    throw new Error('Question ID is required to delete a quiz');
  }

  try {
    const response = await api.delete(
      `/api/v1/admin/quiz-questions/${questionId}`
    );
    return response.data;
  } catch (error) {
    if (error.response) {
      const message =
        error.response.data?.message ||
        `Server error: ${error.response.status}`;
      throw new Error(message);
    } else if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

// --- JSON Operations ---

// Upload quiz JSON
// params: optional object with module_id, chapter_id, unit_id, lesson_id, lesson_content_id (or legacy content_id string)
export const uploadQuizCsv = async (file, params) => {
  if (!file) {
    throw new Error('JSON file is required');
  }

  try {
    const formData = new FormData();
    formData.append('json_file', file);

    if (params != null && params !== '') {
      if (typeof params === 'string') {
        formData.append('content_id', params);
      } else if (typeof params === 'object') {
        if (params.module_id) formData.append('module_id', params.module_id);
        if (params.chapter_id) formData.append('chapter_id', params.chapter_id);
        if (params.unit_id) formData.append('unit_id', params.unit_id);
        if (params.lesson_id) formData.append('lesson_id', params.lesson_id);
        if (params.lesson_content_id) formData.append('lesson_content_id', params.lesson_content_id);
        if (params.content_id) formData.append('content_id', params.content_id);
      }
    }

    const response = await api.post('/api/v1/admin/quiz-questions/upload-json', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 6000000, // 10 minutes - allow large JSON uploads to complete
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      const message = error.response.data?.message || `Server error: ${error.response.status}`;
      throw new Error(message);
    }
    throw new Error(error.message || 'Failed to upload JSON');
  }
};
